import axios from 'axios';

export type OsmosisAIStep = 'getToken' | 'launch';
export type OsmosisAIFlow = 'new-tab' | 'embedded';

/**
 * Sanitized, PII-free metadata describing a failed Osmosis AI launch.
 * Sent to analytics so we never include the raw error message.
 */
export type OsmosisAIErrorMetadata = {
  step?: OsmosisAIStep;
  flow?: OsmosisAIFlow;
  status?: number;
  code: string;
  attempts?: number;
};

type OsmosisAIErrorArgs = {
  step: OsmosisAIStep;
  flow: OsmosisAIFlow;
  status?: number;
  code: string;
  attempts: number;
  message?: string;
};

/**
 * Error thrown when launching Osmosis AI fails. Carries sanitized metadata
 * (step, flow, HTTP status, derived code, retry attempts) for debugging via
 * analytics without leaking the underlying error message or any PII.
 */
export class OsmosisAIError extends Error {
  readonly step: OsmosisAIStep;
  readonly flow: OsmosisAIFlow;
  readonly status?: number;
  readonly code: string;
  readonly attempts: number;

  /** Builds an error from sanitized launch-failure metadata. */
  constructor({
    step,
    flow,
    status,
    code,
    attempts,
    message,
  }: OsmosisAIErrorArgs) {
    super(message ?? `Osmosis AI launch failed at ${step} (${code})`);
    this.name = 'OsmosisAIError';
    this.step = step;
    this.flow = flow;
    this.status = status;
    this.code = code;
    this.attempts = attempts;
  }
}

/** HTTP status of an axios error, if any. */
function getStatus(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined;
}

/** Derives a sanitized, low-cardinality error code from an unknown error. */
export function getErrorCode(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status) return `HTTP_${status}`;
    return err.code ?? 'ERR_NETWORK';
  }
  return 'UNKNOWN';
}

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

type RetryOptions = {
  step: OsmosisAIStep;
  flow: OsmosisAIFlow;
  /** Number of retries after the initial attempt. Defaults to 3. */
  retries?: number;
  /** Base backoff delay in ms (doubled each retry). Defaults to 300. */
  baseDelayMs?: number;
};

/**
 * Runs `fn`, retrying on any error with exponential backoff up to `retries`
 * times. On final failure throws an {@link OsmosisAIError} carrying sanitized
 * metadata.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  { step, flow, retries = 3, baseDelayMs = 300 }: RetryOptions,
): Promise<T> {
  let attempt = 0;

  for (;;) {
    attempt += 1;
    try {
      return await fn();
    } catch (err) {
      if (attempt > retries) {
        throw new OsmosisAIError({
          step,
          flow,
          status: getStatus(err),
          code: getErrorCode(err),
          attempts: attempt,
        });
      }
      await delay(baseDelayMs * 2 ** (attempt - 1));
    }
  }
}

/**
 * Extracts sanitized metadata for analytics from a thrown error, falling back
 * to a generic code for anything that is not an {@link OsmosisAIError}.
 */
export function getErrorMetadata(err: unknown): OsmosisAIErrorMetadata {
  if (err instanceof OsmosisAIError) {
    return {
      step: err.step,
      flow: err.flow,
      status: err.status,
      code: err.code,
      attempts: err.attempts,
    };
  }
  return { code: 'UNKNOWN' };
}
